if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('keyup', (event) => {
          if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
        });
      }

      connectedCallback() {
        this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => this.close());
      }

      open() {
        this.classList.add('active');
        document.body.classList.add('overflow-hidden');
      }

      close() {
        this.classList.remove('active');
        document.body.classList.remove('overflow-hidden');
      }

      renderContents(parsedState) {
        const countdownRemaining = this.getCountdownRemaining();
        const drawerMarkup = parsedState?.sections?.['cart-drawer'];
        if (drawerMarkup) {
          const parsed = new DOMParser().parseFromString(drawerMarkup, 'text/html');
          const replacement = parsed.querySelector('cart-drawer');
          if (replacement) {
            this.innerHTML = replacement.innerHTML;
            this.className = replacement.className;
            this.connectedCallback();
            this.restoreCountdown(countdownRemaining);
          }
        }

        const bubbleMarkup = parsedState?.sections?.['cart-icon-bubble'];
        if (bubbleMarkup) {
          const parsed = new DOMParser().parseFromString(bubbleMarkup, 'text/html');
          const replacement = parsed.querySelector('#shopify-section-cart-icon-bubble') || parsed.body.firstElementChild;
          const current = document.querySelector('#shopify-section-cart-icon-bubble');
          if (replacement && current) current.innerHTML = replacement.innerHTML;
        }

        this.open();
      }

      getCountdownRemaining() {
        const timer = this.querySelector('.cart-timer countdown-timer');
        if (!timer) return null;

        const parts = Array.from(timer.querySelectorAll('span')).map((part) => Number.parseInt(part.textContent, 10));
        if (parts.length >= 2 && parts.every(Number.isFinite)) return parts[0] * 60 + parts[1];

        const remaining = Number.parseInt(timer.dataset.duration, 10);
        return Number.isFinite(remaining) && remaining > 0 ? remaining : null;
      }

      restoreCountdown(remaining) {
        if (!remaining) return;

        this.querySelectorAll('.cart-timer countdown-timer').forEach((timer) => {
          timer.dataset.duration = remaining;
          if (typeof timer.updateTimer === 'function') timer.updateTimer();
        });
      }
    }
  );
}

if (!customElements.get('cart-notification')) {
  customElements.define(
    'cart-notification',
    class CartNotification extends HTMLElement {
      open() {
        this.querySelector('#cart-notification')?.style.removeProperty('display');
      }

      close() {
        const notification = this.querySelector('#cart-notification');
        if (notification) notification.style.display = 'none';
      }
    }
  );
}

if (!customElements.get('cart-remove-button')) {
  customElements.define(
    'cart-remove-button',
    class CartRemoveButton extends HTMLElement {
      connectedCallback() {
        if (this.dataset.localRemoveBound === 'true') return;

        this.dataset.localRemoveBound = 'true';
        this.querySelector('button')?.addEventListener('click', (event) => {
          event.preventDefault();
          const cartItems = this.closest('cart-drawer-items') || document.querySelector('cart-drawer-items');
          cartItems?.updateQuantity(Number(this.dataset.index), 0);
        });
      }
    }
  );
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define(
    'cart-drawer-items',
    class CartDrawerItems extends HTMLElement {
      connectedCallback() {
        if (this.dataset.localCartItemsBound === 'true') return;

        this.dataset.localCartItemsBound = 'true';
        this.addEventListener('change', this.onChange.bind(this));
      }

      onChange(event) {
        const input = event.target;
        if (!input.matches('input[name="updates[]"]')) return;

        this.updateQuantity(Number(input.dataset.index), Number(input.value));
      }

      async updateQuantity(line, quantity) {
        if (!line) return;

        this.setLoading(line, true);
        this.clearLineError(line);

        try {
          const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              line,
              quantity,
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            }),
          });
          const parsedState = await response.json();

          if (!response.ok) {
            this.showLineError(line, parsedState?.description || parsedState?.message || 'Unable to update item.');
            return;
          }

          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer?.renderContents) cartDrawer.renderContents(parsedState);
        } catch (error) {
          this.showLineError(line, 'Unable to update item. Please try again.');
        } finally {
          this.setLoading(line, false);
        }
      }

      setLoading(line, loading) {
        const item = this.querySelector(`#CartDrawer-Item-${line}`);
        item?.querySelector('.loading-overlay')?.classList.toggle('hidden', !loading);
      }

      clearLineError(line) {
        const error = this.querySelector(`#CartDrawer-LineItemError-${line} .cart-item__error-text`);
        if (error) error.textContent = '';
      }

      showLineError(line, message) {
        const error = this.querySelector(`#CartDrawer-LineItemError-${line} .cart-item__error-text`);
        if (error) error.textContent = message;
      }
    }
  );
}

if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      connectedCallback() {
        this.addEventListener('change', this.onVariantChange.bind(this));
        this.variantData = this.getVariantData();
      }

      onVariantChange() {
        this.variantData = this.variantData || this.getVariantData();
        this.options = this.getSelectedOptions();
        this.currentVariant = this.getCurrentVariant();

        this.updateMedia();
        this.updateVariantInputs();
        this.updatePickupAvailability();
        this.updateUrl();
        this.updateShareUrl();
        this.updateProductInfo();
        this.updateAtcState();

        this.dispatchEvent(
          new CustomEvent('variant:change', {
            bubbles: true,
            detail: { variant: this.currentVariant },
          })
        );
      }

      getSelectedOptions() {
        return Array.from(this.querySelectorAll('.product-form__input')).map((optionGroup) => {
          const select = optionGroup.querySelector('select');
          if (select) return select.value;

          const checked = optionGroup.querySelector('input[type="radio"]:checked');
          return checked?.value;
        });
      }

      getCurrentVariant() {
        return this.variantData.find((variant) => {
          return variant.options.every((option, index) => option === this.options[index]);
        });
      }

      getVariantData() {
        const json = this.querySelector('script[type="application/json"]')?.textContent;
        return json ? JSON.parse(json) : [];
      }

      updateVariantInputs() {
        const variantId = this.currentVariant?.id || '';
        document.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, product-form[data-section="${this.cssEscape(this.dataset.section)}"] form`
        ).forEach((form) => {
          const input = form.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      updateUrl() {
        if (this.dataset.updateUrl === 'false' || !this.currentVariant) return;
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
      }

      updateShareUrl() {
        const shareButton = document.querySelector(`share-button`);
        if (!shareButton || !this.currentVariant) return;

        const input = shareButton.querySelector('input');
        if (input) input.value = `${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`;
      }

      updatePickupAvailability() {
        const pickupAvailability = document.querySelector('pickup-availability');
        if (!pickupAvailability || !this.currentVariant) return;

        pickupAvailability.setAttribute('data-variant-id', this.currentVariant.id);
        if (typeof pickupAvailability.fetchAvailability === 'function') {
          pickupAvailability.fetchAvailability(this.currentVariant.id);
        }
      }

      updateMedia() {
        const mediaId = this.currentVariant?.featured_media?.id || this.currentVariant?.featured_image?.id;
        if (!mediaId) return;

        const sectionMediaId = `${this.dataset.section}-${mediaId}`;
        document.querySelectorAll(`media-gallery[data-section="${this.cssEscape(this.dataset.section)}"]`).forEach((gallery) => {
          if (!gallery.querySelector(`[data-media-id="${this.cssEscape(sectionMediaId)}"]`)) return;

          gallery.setActiveMedia?.(sectionMediaId, true, Boolean(gallery.dataset.filteringOption), this.currentVariant);
        });
      }

      async updateProductInfo() {
        if (!this.currentVariant) return;

        try {
          const response = await fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`);
          const text = await response.text();
          const html = new DOMParser().parseFromString(text, 'text/html');

          this.replaceElement(html, `#price-${this.dataset.section}`);
          this.replaceElement(html, `#ProductSubmitButton-${this.dataset.section}`);
          this.replaceElement(html, `#main-atc-price-${this.dataset.section}`);
          this.replaceElement(html, `#sticky-atc-price-${this.dataset.section}`);
          this.replaceElement(html, `#sticky-atc-separate-price-${this.dataset.section}`);
          this.replaceElement(html, `#Quantity-Form-${this.dataset.section}`);
          this.replaceElement(html, `#quantity-breaks-${this.dataset.section}`);

          const sourceInstallments = html.querySelector(`#product-form-installment-${this.dataset.section}`);
          const destinationInstallments = document.querySelector(`#product-form-installment-${this.dataset.section}`);
          if (sourceInstallments && destinationInstallments) destinationInstallments.innerHTML = sourceInstallments.innerHTML;
        } catch (error) {
          this.updateInlinePrices();
        }
      }

      replaceElement(html, selector) {
        const source = html.querySelector(selector);
        const destination = document.querySelector(selector);
        if (!source || !destination) return;

        if (destination.matches('quantity-breaks')) {
          destination.replaceWith(source);
          return;
        }

        destination.innerHTML = source.innerHTML;
      }

      updateInlinePrices() {
        const money = this.formatMoney(this.currentVariant.price);
        document.querySelectorAll(`.main-atc-price-span-${this.dataset.section}`).forEach((element) => {
          element.textContent = money;
        });
      }

      updateAtcState() {
        const buttons = document.querySelectorAll(`#ProductSubmitButton-${this.dataset.section}, #SectionAtcBtn-${this.dataset.section}`);
        const unavailable = !this.currentVariant || !this.currentVariant.available;

        buttons.forEach((button) => {
          button.toggleAttribute('disabled', unavailable);
          button.toggleAttribute('aria-disabled', unavailable);
          if (unavailable) button.dataset.unavailable = 'true';
          else delete button.dataset.unavailable;

          const label = button.querySelector('.main-atc__label__line-1 span:last-child, .button__label');
          if (!label) return;
          if (!this.currentVariant) label.textContent = window.variantStrings?.unavailable || 'Unavailable';
          else if (!this.currentVariant.available) label.textContent = window.variantStrings?.soldOut || 'Sold out';
          else if (button.id.startsWith('ProductSubmitButton')) label.textContent = button.dataset.customLabel || window.variantStrings?.addToCart || 'Add to cart';
        });
      }

      cssEscape(value) {
        if (window.CSS?.escape) return window.CSS.escape(value);
        return String(value).replace(/"/g, '\\"');
      }

      formatMoney(cents) {
        if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);

        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
        }).format(cents / 100);
      }
    }
  );
}

(() => {
  if (window.__localVariantPriceSyncBound) return;
  window.__localVariantPriceSyncBound = true;

  document.addEventListener('change', (event) => {
    const picker = event.target.closest?.('variant-selects[data-section]');
    if (!picker) return;

    const variant = getPickerVariant(picker);
    if (!variant) return;

    syncPickerForms(picker, variant);
    syncPickerPrices(picker, variant);
    syncQuantityBreaks(picker, variant);
    setTimeout(() => {
      const latestVariant = getPickerVariant(picker);
      if (latestVariant?.id === variant.id) {
        syncPickerPrices(picker, latestVariant);
        syncQuantityBreaks(picker, latestVariant);
      }
    }, 350);
  });

  function getPickerVariant(picker) {
    const selectedOptions = Array.from(picker.querySelectorAll('.product-form__input')).map((optionGroup) => {
      const select = optionGroup.querySelector('select');
      if (select) return select.value;

      const checked = optionGroup.querySelector('input[type="radio"]:checked');
      return checked?.value;
    });

    let variants = [];
    try {
      const json = picker.querySelector('script[type="application/json"]')?.textContent;
      variants = json ? JSON.parse(json) : [];
    } catch (error) {
      return null;
    }

    return variants.find((variant) => variant.options.every((option, index) => option === selectedOptions[index]));
  }

  function syncPickerForms(picker, variant) {
    const section = picker.dataset.section;
    if (!section) return;

    document.querySelectorAll(`#product-form-${cssEscape(section)}, #product-form-installment-${cssEscape(section)}, product-form[data-section="${cssEscape(section)}"] form`).forEach((form) => {
      const input = form.querySelector('input[name="id"]');
      if (!input) return;

      input.value = variant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function syncPickerPrices(picker, variant) {
    const section = picker.dataset.section;
    if (!section) return;

    const price = Number(variant.price || 0);
    const compareAtPrice = Number(variant.compare_at_price || 0);
    const money = formatMoney(price);
    const compareMoney = formatMoney(compareAtPrice);
    const isOnSale = compareAtPrice > price;

    document.querySelectorAll(`.main-atc-price-span-${cssEscape(section)}`).forEach((element) => {
      element.textContent = money;
    });

    document.querySelectorAll(`#price-${cssEscape(section)} .price`).forEach((priceElement) => {
      priceElement.classList.toggle('price--on-sale', isOnSale);
      priceElement.classList.toggle('price--sold-out', variant.available === false);

      priceElement.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale').forEach((element) => {
        element.textContent = money;
      });

      priceElement.querySelectorAll('.price__compare-price .price-item--regular').forEach((element) => {
        element.textContent = isOnSale ? compareMoney : '';
      });

      priceElement.querySelectorAll('.badge__percentage-saved').forEach((element) => {
        element.textContent = isOnSale ? Math.floor(((compareAtPrice - price) * 100) / compareAtPrice) : '0';
      });

      priceElement.querySelectorAll('.badge__money-saved').forEach((element) => {
        element.textContent = isOnSale ? formatMoneyWithoutTrailingZeros(compareAtPrice - price) : formatMoneyWithoutTrailingZeros(0);
      });
    });
  }

  function syncQuantityBreaks(picker, variant) {
    const section = picker.dataset.section;
    if (!section) return;

    document.querySelectorAll(`quantity-breaks[data-section="${cssEscape(section)}"]`).forEach((quantityBreaks) => {
      quantityBreaks.syncSelectorsToMainVariant?.(variant);
      quantityBreaks.updateOfferPrices?.();
    });
  }

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(cents / 100);
  }

  function formatMoneyWithoutTrailingZeros(cents) {
    return formatMoney(cents).replace(/\.00(?!\d)/, '');
  }

  function cssEscape(value) {
    if (window.CSS?.escape) return window.CSS.escape(value);
    return String(value).replace(/"/g, '\\"');
  }
})();

if (!customElements.get('quantity-input')) {
  customElements.define(
    'quantity-input',
    class QuantityInput extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="number"]');
        if (!this.input || this.dataset.localQuantityInputBound === 'true') return;

        this.dataset.localQuantityInputBound = 'true';
        this.querySelectorAll('button[name="plus"], button[name="minus"]').forEach((button) => {
          button.addEventListener('click', this.onButtonClick.bind(this));
        });
        this.input.addEventListener('change', this.onInputChange.bind(this));
        document.addEventListener('variant:change', this.onVariantChange.bind(this));
        this.updateButtonState();
      }

      onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.currentValue();
        const step = this.step();
        const nextValue = event.currentTarget.name === 'plus' ? previousValue + step : previousValue - step;

        this.input.value = this.clamp(nextValue);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onInputChange() {
        this.input.value = this.clamp(this.currentValue());
        this.updateButtonState();
      }

      onVariantChange(event) {
        if (!event.detail?.variant || event.target?.dataset?.section !== this.dataset.section) return;

        const rule = event.detail.variant.quantity_rule || {};
        const min = Number(rule.min || 1);
        const increment = Number(rule.increment || 1);
        const max = rule.max ? Number(rule.max) : null;

        this.input.dataset.min = min;
        this.input.min = min;
        this.input.step = increment;
        if (max) {
          this.input.dataset.max = max;
          this.input.max = max;
        } else {
          delete this.input.dataset.max;
          this.input.removeAttribute('max');
        }

        if (this.currentValue() < min) this.input.value = min;
        this.updateButtonState();
      }

      currentValue() {
        return Number.parseInt(this.input.value, 10) || this.min();
      }

      min() {
        return Number.parseInt(this.input.dataset.min || this.input.min, 10) || 1;
      }

      max() {
        const value = Number.parseInt(this.input.dataset.max || this.input.max, 10);
        return Number.isFinite(value) ? value : null;
      }

      step() {
        return Number.parseInt(this.input.step, 10) || 1;
      }

      clamp(value) {
        const min = this.min();
        const max = this.max();
        const step = this.step();
        let nextValue = Math.max(value, min);

        if (max) nextValue = Math.min(nextValue, max);
        if (step > 1) {
          const offset = nextValue - min;
          nextValue = min + Math.round(offset / step) * step;
          if (max) nextValue = Math.min(nextValue, max);
        }

        return nextValue;
      }

      updateButtonState() {
        const value = this.currentValue();
        const min = this.min();
        const max = this.max();

        this.querySelector('button[name="minus"]')?.toggleAttribute('disabled', value <= min);
        this.querySelector('button[name="plus"]')?.toggleAttribute('disabled', Boolean(max && value >= max));
      }
    }
  );
}

if (!customElements.get('quantity-breaks')) {
  customElements.define(
    'quantity-breaks',
    class QuantityBreaks extends HTMLElement {
      connectedCallback() {
        if (this.dataset.localQuantityBreaksBound === 'true') return;

        this.dataset.localQuantityBreaksBound = 'true';
        this.boundMainVariantChange = this.onMainVariantChange.bind(this);
        this.variantData = this.getVariantData();
        this.addEventListener('change', this.onChange.bind(this));
        this.querySelectorAll('.quantity-break__upsell[data-toggle="true"]').forEach((upsell) => {
          upsell.addEventListener('click', (event) => {
            if (event.target.closest('a, button, input, select')) return;
            upsell.dataset.selected = upsell.dataset.selected === 'true' ? 'false' : 'true';
          });
        });
        this.querySelectorAll('.quantity-break__selector-item').forEach((item) => this.updateSelectorItem(item));
        this.ensureSelectedOffer();
        this.syncSelectorsToMainVariant();
        this.updateUpsells();
        this.updateOfferPrices();
        document.addEventListener('variant:change', this.boundMainVariantChange);
      }

      disconnectedCallback() {
        if (this.boundMainVariantChange) {
          document.removeEventListener('variant:change', this.boundMainVariantChange);
        }
      }

      onChange(event) {
        if (event.target.matches('input[name="quantity"]')) {
          this.updateUpsells();
          this.updateOfferPrices();
          this.dispatchEvent(
            new CustomEvent('quantity-break:change', {
              bubbles: true,
              detail: { quantity: Number.parseInt(event.target.value, 10) || 1 },
            })
          );
          return;
        }

        if (event.target.matches('.quantity-break__variant-select')) {
          this.updateSelectorItem(event.target.closest('.quantity-break__selector-item'));
          this.updateOfferPrices();
        }
      }

      onMainVariantChange(event) {
        if (!event.detail?.variant || event.target?.dataset?.section !== this.dataset.section) return;

        this.syncSelectorsToMainVariant(event.detail.variant);
        this.updateOfferPrices();
      }

      syncSelectorsToMainVariant(variant = null) {
        const mainVariant = variant || this.getMainVariant();
        if (!mainVariant) return;

        this.querySelectorAll('.quantity-break__selector-item').forEach((item) => {
          item.dataset.selectedId = mainVariant.id;
          item.querySelectorAll('.quantity-break__variant-select').forEach((select, index) => {
            const value = mainVariant.options?.[index];
            if (value && Array.from(select.options).some((option) => option.value === value && !option.disabled)) {
              select.value = value;
            }
            select.dataset.productId = mainVariant.id;
          });
          this.updateSelectorItem(item);
        });
      }

      getMainVariant() {
        const formVariantId = document.querySelector(`product-form[data-section="${this.cssEscape(this.dataset.section)}"] input[name="id"], #product-form-${this.cssEscape(this.dataset.section)} input[name="id"]`)?.value;
        if (formVariantId) {
          const variant = this.variantData.find((candidate) => String(candidate.id) === String(formVariantId));
          if (variant) return variant;
        }

        const picker = document.querySelector(`variant-selects[data-section="${this.cssEscape(this.dataset.section)}"]`);
        if (!picker) return null;

        const selectedOptions = Array.from(picker.querySelectorAll('.product-form__input')).map((optionGroup) => {
          const select = optionGroup.querySelector('select');
          if (select) return select.value;

          const checked = optionGroup.querySelector('input[type="radio"]:checked');
          return checked?.value;
        });

        return this.variantData.find((candidate) => candidate.options.every((option, index) => option === selectedOptions[index]));
      }

      ensureSelectedOffer() {
        if (this.querySelector('input[name="quantity"]:checked')) return;
        const firstOffer = this.querySelector('input[name="quantity"]');
        if (firstOffer) firstOffer.checked = true;
      }

      updateSelectorItem(item) {
        if (!item) return;
        const selectedOptions = Array.from(item.querySelectorAll('.quantity-break__variant-select')).map((select) => select.value);
        const variant = this.variantData.find((candidate) => {
          return candidate.options.every((option, index) => option === selectedOptions[index]);
        });

        if (!variant) return;
        item.dataset.selectedId = variant.id;
        item.querySelectorAll('.quantity-break__variant-select').forEach((select) => {
          select.dataset.productId = variant.id;
        });

        const image = item.querySelector('.quantity-break__selector-item__image img');
        const imageUrl = variant.featured_image?.src || variant.featured_image?.url;
        if (image && imageUrl) {
          image.src = imageUrl;
          image.alt = variant.featured_image?.alt || '';
        }
      }

      updateOfferPrices() {
        this.querySelectorAll('.quantity-break').forEach((offer) => this.updateOfferPrice(offer));
      }

      updateOfferPrice(offer) {
        const selectorItems = Array.from(offer.querySelectorAll('.quantity-break__selector-item[data-selected-id]'));
        const quantity = Number.parseInt(offer.dataset.quantity, 10) || selectorItems.length || 1;
        const variants = selectorItems.length
          ? selectorItems
              .map((item) => this.variantData.find((variant) => String(variant.id) === String(item.dataset.selectedId)))
              .filter(Boolean)
          : Array.from({ length: quantity }, () => this.getMainVariant()).filter(Boolean);
        if (!variants.length) return;

        const percentageLeft = Number.parseFloat(offer.dataset.percentageLeft || 1);
        const fixedDiscount = Number.parseFloat(offer.dataset.fixedDiscount || 0);
        const compareMode = offer.dataset.compareMode || 'price';
        const basePriceTotal = variants.reduce((total, variant) => total + Number(variant.price || 0), 0);
        const comparePriceTotal = variants.reduce((total, variant) => {
          const variantComparePrice = Number(variant.compare_at_price || 0);
          if (compareMode === 'compare_price' && variantComparePrice > Number(variant.price || 0)) return total + variantComparePrice;
          return total + Number(variant.price || 0);
        }, 0);
        const price = Math.max(Math.round(basePriceTotal * percentageLeft - fixedDiscount), 0);
        const amountSaved = Math.max(comparePriceTotal - price, 0);
        const amountSavedRounded = Math.round(amountSaved / 100) * 100;
        const priceEach = quantity > 0 ? Math.round(price / quantity) : price;
        const comparePriceEach = quantity > 0 ? Math.round(comparePriceTotal / quantity) : comparePriceTotal;
        const percentageSaved = comparePriceTotal > 0 ? Math.round((amountSaved / comparePriceTotal) * 100) : 0;

        offer.querySelectorAll('.variant-price-update[data-text]').forEach((element) => {
          element.innerHTML = this.renderPriceText(element.dataset.text, {
            quantity,
            price,
            comparePrice: comparePriceTotal,
            amountSaved,
            amountSavedRounded,
            priceEach,
            comparePriceEach,
            percentageSaved,
          });

          if (element.classList.contains('quantity-break__compare-price')) {
            element.classList.toggle('hidden', comparePriceTotal <= price);
          }
        });
      }

      renderPriceText(text, values) {
        return String(text || '')
          .replace(/\[quantity\]/g, values.quantity)
          .replace(/\[price\]/g, this.formatMoneyWithoutTrailingZeros(values.price))
          .replace(/\[compare_price\]/g, this.formatMoneyWithoutTrailingZeros(values.comparePrice))
          .replace(/\[amount_saved\]/g, this.formatMoneyWithoutTrailingZeros(values.amountSaved))
          .replace(/\[amount_saved_rounded\]/g, this.formatMoneyWithoutTrailingZeros(values.amountSavedRounded))
          .replace(/\[price_each\]/g, this.formatMoneyWithoutTrailingZeros(values.priceEach))
          .replace(/\[compare_price_each\]/g, this.formatMoneyWithoutTrailingZeros(values.comparePriceEach))
          .replace(/\[percentage_saved\]/g, `${values.percentageSaved}%`);
      }

      formatMoneyWithoutTrailingZeros(cents) {
        return this.formatMoney(cents).replace(/\.00(?!\d)/, '');
      }

      updateUpsells() {
        const checkedOffer = this.querySelector('input[name="quantity"]:checked');
        const offerIndex = checkedOffer ? Number.parseInt(checkedOffer.dataset.input?.replace('input_', ''), 10) : 1;

        this.querySelectorAll('.quantity-break__upsell').forEach((upsell) => {
          const minOffer = Number.parseInt(upsell.dataset.minOffer, 10) || 1;
          upsell.classList.toggle('hidden', offerIndex < minOffer);
        });
      }

      getVariantData() {
        const json = this.querySelector('script[type="application/json"]')?.textContent;
        return json ? JSON.parse(json) : [];
      }

      formatMoney(cents) {
        if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);

        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
        }).format(cents / 100);
      }

      cssEscape(value) {
        if (window.CSS?.escape) return window.CSS.escape(value);
        return String(value).replace(/"/g, '\\"');
      }
    }
  );
}

class LocalBundleOffer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.localBundleOfferBound === 'true') return;

    this.dataset.localBundleOfferBound = 'true';
    this.addEventListener('change', this.onChange.bind(this));
    this.refreshBundleItems();
  }

  onChange(event) {
    if (event.target.matches('.bundle-deals__checkbox-js')) {
      this.updateSelection(event.target);
      return;
    }

    if (event.target.matches('.bundle-deals__variant-selects-js select')) {
      this.updateVariantPicker(event.target.closest('.bundle-deals__variant-selects-js'));
    }
  }

  refreshBundleItems() {
    this.querySelectorAll('.bundle-deals__checkbox-js').forEach((checkbox) => this.updateSelection(checkbox));
    this.querySelectorAll('.bundle-deals__variant-selects-js').forEach((picker) => this.updateVariantPicker(picker));
  }

  updateSelection(checkbox) {
    const upsell = checkbox.closest('.upsell');
    if (!upsell) return;

    upsell.dataset.selected = checkbox.checked ? 'true' : 'false';
    checkbox.dataset.checked = checkbox.checked ? 'true' : 'false';
    this.updateTotalPrice();
  }

  updateVariantPicker(picker) {
    if (!picker) return;

    const variants = this.getVariantData(picker);
    const selectedOptions = Array.from(picker.querySelectorAll('select')).map((select) => select.value);
    const variant = variants.find((candidate) => {
      return candidate.options.every((option, index) => option === selectedOptions[index]);
    });
    if (!variant) return;

    const upsell = picker.closest('.upsell');
    const checkbox = upsell?.querySelector('.bundle-deals__checkbox-js');
    if (upsell) upsell.dataset.id = variant.id;
    if (checkbox) checkbox.dataset.id = variant.id;

    this.updateVariantImage(picker, variant);
    this.updateVariantPrice(picker, variant);
    this.updateTotalPrice();
  }

  updateVariantImage(picker, variant) {
    const image = picker.closest('.bundle-deals__product-js')?.querySelector('.bundle-deals__media-item-img-js');
    const imageUrl = variant.featured_image?.src || variant.featured_image?.url;
    if (!image || !imageUrl) return;

    image.src = imageUrl;
    image.alt = variant.featured_image?.alt || '';
  }

  updateVariantPrice(picker, variant) {
    const upsell = picker.closest('.upsell');
    const checkbox = upsell?.querySelector('.bundle-deals__checkbox-js');
    if (!upsell) return;

    const percentageLeft = Number.parseFloat(picker.dataset.percentageLeft || upsell.dataset.percentageLeft || 1);
    const fixedDiscount = Number.parseFloat(picker.dataset.fixedDiscount || upsell.dataset.fixedDiscount || 0);
    const price = Math.max(Math.round(variant.price * percentageLeft - fixedDiscount), 0);
    const baseComparePrice = Number(variant.compare_at_price) > Number(variant.price) ? Number(variant.compare_at_price) : Number(variant.price);

    if (checkbox) {
      checkbox.dataset.price = price;
      checkbox.dataset.comparePrice = baseComparePrice;
    }
    upsell.dataset.price = price;
    upsell.dataset.comparePrice = baseComparePrice;

    upsell.querySelectorAll('.bundle-deals__price-js').forEach((element) => {
      element.textContent = this.formatMoney(price);
    });
    upsell.querySelectorAll('.bundle-deals__compare-price-js').forEach((element) => {
      element.textContent = baseComparePrice > price ? this.formatMoney(baseComparePrice) : '';
    });
  }

  updateTotalPrice() {
    const totalPriceElement = this.querySelector('.bundle-deals__total-price-js');
    const totalCompareElement = this.querySelector('.bundle-deals__total-compare-price-js');
    if (!totalPriceElement && !totalCompareElement) return;

    let totalPrice = 0;
    let totalComparePrice = 0;

    this.querySelectorAll('.upsell[data-selected="true"]').forEach((upsell) => {
      const checkbox = upsell.querySelector('.bundle-deals__checkbox-js');
      if (checkbox && !checkbox.checked) return;

      totalPrice += Number.parseFloat(checkbox?.dataset.price || upsell.dataset.price || 0);
      totalComparePrice += Number.parseFloat(checkbox?.dataset.comparePrice || upsell.dataset.comparePrice || 0);
    });

    const totalPercentageLeft = Number.parseFloat(this.dataset.percentageLeft || 1);
    const totalFixedDiscount = Number.parseFloat(this.dataset.fixedDiscount || 0);
    totalPrice = Math.max(Math.round(totalPrice * totalPercentageLeft - totalFixedDiscount), 0);

    if (totalPriceElement) totalPriceElement.textContent = this.formatMoney(totalPrice);
    if (totalCompareElement) totalCompareElement.textContent = totalComparePrice > totalPrice ? this.formatMoney(totalComparePrice) : '';
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(cents / 100);
  }

  getVariantData(picker) {
    const json = picker.querySelector('script[type="application/json"]')?.textContent;
    return json ? JSON.parse(json) : [];
  }
}

if (!customElements.get('bundle-deals')) {
  customElements.define('bundle-deals', class BundleDeals extends LocalBundleOffer {});
}

if (!customElements.get('main-bundle-offer')) {
  customElements.define('main-bundle-offer', class MainBundleOffer extends LocalBundleOffer {});
}

if (!customElements.get('comparison-slider')) {
  customElements.define(
    'comparison-slider',
    class ComparisonSlider extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('.comparison-slider__input');
        if (!this.input || this.bound) return;

        this.bound = true;
        this.onInput = this.onInput.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerEnd = this.onPointerEnd.bind(this);

        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('change', this.onInput);
        this.addEventListener('pointerdown', this.onPointerDown);
        this.onInput();
      }

      disconnectedCallback() {
        if (!this.input || !this.bound) return;

        this.input.removeEventListener('input', this.onInput);
        this.input.removeEventListener('change', this.onInput);
        this.removeEventListener('pointerdown', this.onPointerDown);
        this.removeEventListener('pointermove', this.onPointerMove);
        this.removeEventListener('pointerup', this.onPointerEnd);
        this.removeEventListener('pointercancel', this.onPointerEnd);
        this.bound = false;
      }

      onInput() {
        this.setPosition(Number.parseFloat(this.input.value));
      }

      onPointerDown(event) {
        if (event.button !== undefined && event.button !== 0) return;
        if (event.target.closest('a, button')) return;

        this.setPositionFromPointer(event);
        this.setPointerCapture?.(event.pointerId);
        this.addEventListener('pointermove', this.onPointerMove);
        this.addEventListener('pointerup', this.onPointerEnd);
        this.addEventListener('pointercancel', this.onPointerEnd);
      }

      onPointerMove(event) {
        this.setPositionFromPointer(event);
      }

      onPointerEnd(event) {
        this.releasePointerCapture?.(event.pointerId);
        this.removeEventListener('pointermove', this.onPointerMove);
        this.removeEventListener('pointerup', this.onPointerEnd);
        this.removeEventListener('pointercancel', this.onPointerEnd);
      }

      setPositionFromPointer(event) {
        const rect = this.getBoundingClientRect();
        if (!rect.width) return;

        const rawPosition = ((event.clientX - rect.left) / rect.width) * 100;
        const position = this.isRtl() ? 100 - rawPosition : rawPosition;
        this.setPosition(position);
      }

      setPosition(value) {
        const position = this.clamp(value);
        this.style.setProperty('--position', `${position}%`);

        if (this.input) {
          this.input.value = String(position);
          this.input.setAttribute('aria-valuenow', String(Math.round(position)));
        }
      }

      clamp(value) {
        return Math.max(0, Math.min(100, Number.isFinite(value) ? value : 50));
      }

      isRtl() {
        return document.documentElement.dir === 'rtl' || document.documentElement.dataset.rtl === 'true';
      }
    }
  );
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form || this.form.dataset.localProductFormBound === 'true') return;
        this.form.dataset.localProductFormBound = 'true';
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
        this.querySelectorAll('[data-skip-cart-button="true"]').forEach((button) => {
          button.addEventListener('click', this.handleSubmit.bind(this));
        });
        this.querySelectorAll('.shopify-payment-button').forEach((buttonWrapper) => {
          buttonWrapper.addEventListener(
            'click',
            () => {
              this.paymentButtonClicked = true;
              window.setTimeout(() => {
                this.paymentButtonClicked = false;
              }, 500);
            },
            true
          );
        });
      }

      async handleSubmit(event) {
        const submitter = event?.submitter || event?.currentTarget || null;

        if (this.paymentButtonClicked) return;
        if (submitter?.closest?.('.shopify-payment-button')) return;
        if (event?.type === 'submit' && submitter && submitter.name !== 'add') return;

        event?.preventDefault?.();

        const form = this.form || this.querySelector('form');
        if (!form) return;

        const submitButton = submitter?.matches?.('button') ? submitter : form.querySelector('[type="submit"][name="add"]');
        const variantInput = form.querySelector('[name="id"]');
        const skipCart = submitButton?.dataset.skipCartButton === 'true' || variantInput?.dataset.skipCart === 'true';

        this.setLoading(submitButton, true);
        this.clearError();

        try {
          this.syncSelectedVariant(form);
          const request = this.buildAddRequest(form, skipCart);

          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: request.headers,
            body: request.body,
          });

          const parsedState = await this.parseResponse(response);
          if (!response.ok) {
            this.showError(parsedState?.description || parsedState?.message || window.variantStrings?.soldOut || 'Unable to add item.');
            return;
          }

          if (skipCart) {
            const rootUrl = window.Shopify?.routes?.root || '/';
            window.location.href = `${rootUrl}checkout`;
            return;
          }

          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer?.renderContents) {
            cartDrawer.renderContents(parsedState);
          } else {
            window.location.href = window.routes?.cart_url || '/cart';
          }
        } catch (error) {
          this.showError('Unable to add item. Please try again.');
        } finally {
          this.setLoading(submitButton, false);
        }
      }

      syncSelectedVariant(form) {
        const formId = form.getAttribute('id') || '';
        const section = this.dataset.section || formId.replace('product-form-', '');
        if (!section) return;

        const picker = document.querySelector(`variant-selects[data-section="${this.cssEscape(section)}"]`);
        const input = form.querySelector('input[name="id"]');
        if (!picker || !input) return;

        const selectedOptions = Array.from(picker.querySelectorAll('.product-form__input')).map((optionGroup) => {
          const select = optionGroup.querySelector('select');
          if (select) return select.value;

          const checked = optionGroup.querySelector('input[type="radio"]:checked');
          return checked?.value;
        });
        let variants = [];
        try {
          const json = picker.querySelector('script[type="application/json"]')?.textContent;
          variants = json ? JSON.parse(json) : [];
        } catch (error) {
          return;
        }
        const variant = variants.find((candidate) => {
          return candidate.options.every((option, index) => option === selectedOptions[index]);
        });

        if (!variant?.id) return;
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      buildAddRequest(form, skipCart) {
        const multiLineItems = this.getQuantityBreakItems(form) || this.getBundleOfferItems(form);

        if (multiLineItems) {
          const payload = { items: multiLineItems };
          if (!skipCart) {
            payload.sections = ['cart-drawer', 'cart-icon-bubble'];
            payload.sections_url = window.location.pathname;
          }

          return {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          };
        }

        const formData = new FormData(form);
        if (!skipCart) {
          formData.append('sections', 'cart-drawer,cart-icon-bubble');
          formData.append('sections_url', window.location.pathname);
        }

        return {
          headers: { Accept: 'application/json' },
          body: formData,
        };
      }

      getBundleOfferItems(form) {
        const offer = this.getBundleOfferElement(form);
        if (!offer) return null;

        offer.refreshBundleItems?.();

        const properties = this.getProperties(form);
        const sellingPlan = form.querySelector('[name="selling_plan"]')?.value;
        const selectedItems = Array.from(offer.querySelectorAll('.upsell[data-selected="true"]'));
        const groupedItems = new Map();

        selectedItems.forEach((item) => {
          const checkbox = item.querySelector('.bundle-deals__checkbox-js');
          if (checkbox && !checkbox.checked) return;

          const id = Number(item.dataset.id || checkbox?.dataset.id);
          if (!id) return;

          const key = [id, sellingPlan || '', JSON.stringify(properties || {})].join('|');
          if (!groupedItems.has(key)) {
            const line = { id, quantity: 0 };
            if (sellingPlan) line.selling_plan = sellingPlan;
            if (properties) line.properties = properties;
            groupedItems.set(key, line);
          }

          groupedItems.get(key).quantity += 1;
        });

        const items = Array.from(groupedItems.values()).filter((item) => item.quantity > 0);
        return items.length ? items : null;
      }

      getBundleOfferElement(form) {
        const formId = form.getAttribute('id') || '';
        const section = this.dataset.section || formId.replace('product-form-', '') || '';
        if (!section) return null;

        const exactOffer = document.querySelector(`bundle-deals[data-section="${this.cssEscape(section)}"], main-bundle-offer[data-section="${this.cssEscape(section)}"]`);
        if (exactOffer) return exactOffer;

        const productSection = formId.startsWith('product-form-') ? formId.replace('product-form-', '') : section;
        return document.querySelector(`main-bundle-offer[id^="MainBundleOffer-${this.cssEscape(productSection)}-"]`);
      }

      getQuantityBreakItems(form) {
        const formId = form.getAttribute('id') || '';
        const section = this.dataset.section || formId.replace('product-form-', '');
        const quantityBreaks = document.querySelector(`quantity-breaks[data-section="${section}"]`) || document.querySelector(`#quantity-breaks-${section}`);
        const checkedInput = quantityBreaks?.querySelector('input[name="quantity"]:checked');
        if (!quantityBreaks || !checkedInput) return null;

        const selectedOffer = checkedInput.nextElementSibling?.matches?.('.quantity-break')
          ? checkedInput.nextElementSibling
          : quantityBreaks.querySelector(`label[for="${this.cssEscape(checkedInput.id)}"]`);
        const selectorItems = Array.from(selectedOffer?.querySelectorAll('.quantity-break__selector-item[data-selected-id]') || []);
        const properties = this.getProperties(form);
        const sellingPlan = form.querySelector('[name="selling_plan"]')?.value;
        const groupedItems = new Map();

        if (!selectorItems.length) {
          const id = Number(form.querySelector('[name="id"]')?.value);
          const quantity = Number.parseInt(checkedInput.value || selectedOffer?.dataset.quantity, 10) || 1;
          if (!id || quantity < 1) return null;

          const line = { id, quantity };
          if (sellingPlan) line.selling_plan = sellingPlan;
          if (properties) line.properties = properties;

          return [line];
        }

        selectorItems.forEach((item) => {
          const id = Number(item.dataset.selectedId);
          if (!id) return;

          const key = [id, sellingPlan || '', JSON.stringify(properties || {})].join('|');
          if (!groupedItems.has(key)) {
            const line = { id, quantity: 0 };
            if (sellingPlan) line.selling_plan = sellingPlan;
            if (properties) line.properties = properties;
            groupedItems.set(key, line);
          }

          groupedItems.get(key).quantity += 1;
        });

        const items = Array.from(groupedItems.values()).filter((item) => item.quantity > 0);
        return items.length ? items : null;
      }

      async parseResponse(response) {
        const text = await response.text();
        if (!text) return {};

        try {
          return JSON.parse(text);
        } catch (error) {
          return {
            message: text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim(),
          };
        }
      }

      getProperties(form) {
        const properties = {};

        new FormData(form).forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value !== '') properties[match[1]] = value;
        });

        return Object.keys(properties).length ? properties : null;
      }

      cssEscape(value) {
        if (window.CSS?.escape) return window.CSS.escape(value);
        return String(value).replace(/"/g, '\\"');
      }

      setLoading(button, loading) {
        if (!button) return;
        button.classList.toggle('loading', loading);
        button.toggleAttribute('aria-disabled', loading);
        button.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !loading);
      }

      clearError() {
        const wrapper = this.querySelector('.product-form__error-message-wrapper');
        const message = this.querySelector('.product-form__error-message');
        if (wrapper) wrapper.hidden = true;
        if (message) message.textContent = '';
      }

      showError(errorMessage) {
        const wrapper = this.querySelector('.product-form__error-message-wrapper');
        const message = this.querySelector('.product-form__error-message');
        if (message) message.textContent = errorMessage;
        if (wrapper) wrapper.hidden = false;
      }
    }
  );
}
